import React, { useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { submitEnquiry } from '../../mockData';
import { buildWhatsAppUrl, trackWhatsAppClick } from '@/lib/whatsapp';

const WHATSAPP_LINK = buildWhatsAppUrl({ variant: 'default' });

const EnquiryForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    studentOrParent: '',
    service: '',
    yearLevelOrBand: '',
    helpNeeded: '',
    preferredTime: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFormChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await submitEnquiry(formData);
      if (result.success) {
        toast.success('Thank you! Your enquiry has been submitted successfully. We\'ll contact you within 24 hours.');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          studentOrParent: '',
          service: '',
          yearLevelOrBand: '',
          helpNeeded: '',
          preferredTime: '',
          message: ''
        });
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="enquiry-form" className="section-cream">
      <div className="section-container">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="accent-bar"></div>
          <h2 className="heading-lg mb-6">Book Your Trial Session</h2>
          <p className="body-lg mb-8" style={{ color: 'var(--text-grey)' }}>
            Fill out the form below and we'll get back to you within 24 hours to schedule your trial lesson.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <p className="text-lg font-semibold" style={{ color: 'var(--text-charcoal)' }}>
              Prefer instant messaging?
            </p>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackWhatsAppClick('enquiry-form', 'default')}
              data-testid="enquiry-whatsapp-btn"
            >
              <Button className="btn-teal">
                <MessageCircle className="w-5 h-5" />
                Chat with us on WhatsApp
              </Button>
            </a>
          </div>
        </div>
        <Card className="max-w-2xl mx-auto p-8" style={{ background: 'var(--bg-white)' }}>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="fullName" className="form-label">Full Name *</Label>
                <Input
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleFormChange}
                  required
                  className="form-input"
                  placeholder="Enter your full name"
                />
              </div>
              <div>
                <Label htmlFor="email" className="form-label">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  required
                  className="form-input"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="phone" className="form-label">Phone Number *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleFormChange}
                  required
                  className="form-input"
                  placeholder="04XX XXX XXX"
                />
              </div>
              <div>
                <Label htmlFor="studentOrParent" className="form-label">I am a *</Label>
                <select
                  id="studentOrParent"
                  name="studentOrParent"
                  value={formData.studentOrParent}
                  onChange={handleFormChange}
                  required
                  className="form-select"
                >
                  <option value="">Select...</option>
                  <option value="Student">Student</option>
                  <option value="Parent">Parent</option>
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="service" className="form-label">Service Needed *</Label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleFormChange}
                  required
                  className="form-select"
                >
                  <option value="">Select...</option>
                  <option value="IELTS Coaching">IELTS Coaching</option>
                  <option value="VCE / Year 12 Tutoring">VCE / Year 12 Tutoring</option>
                  <option value="GMAT Coaching">GMAT Coaching</option>
                  <option value="GRE Coaching">GRE Coaching</option>
                  <option value="SAT Coaching">SAT Coaching</option>
                  <option value="PTE Coaching">PTE Coaching</option>
                </select>
              </div>
              <div>
                <Label htmlFor="yearLevelOrBand" className="form-label">Year Level / Target Band *</Label>
                <Input
                  id="yearLevelOrBand"
                  name="yearLevelOrBand"
                  value={formData.yearLevelOrBand}
                  onChange={handleFormChange}
                  required
                  className="form-input"
                  placeholder="e.g., Year 12 or Band 7"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="helpNeeded" className="form-label">Main Area of Help Needed *</Label>
              <Input
                id="helpNeeded"
                name="helpNeeded"
                value={formData.helpNeeded}
                onChange={handleFormChange}
                required
                className="form-input"
                placeholder="e.g., Essay writing, IELTS Speaking, SAC preparation"
              />
            </div>

            <div>
              <Label htmlFor="preferredTime" className="form-label">Preferred Lesson Time</Label>
              <Input
                id="preferredTime"
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleFormChange}
                className="form-input"
                placeholder="e.g., Weekday evenings, Saturday mornings"
              />
            </div>

            <div>
              <Label htmlFor="message" className="form-label">Additional Message</Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleFormChange}
                className="form-textarea"
                rows={4}
                placeholder="Tell us more about your goals or any questions you have..."
              />
            </div>

            <Button type="submit" disabled={isSubmitting} className="btn-primary w-full text-lg py-6">
              {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
            </Button>
          </form>
        </Card>
      </div>
    </section>
  );
};

export default EnquiryForm;
